// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ImportList.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Column } from 'share/components/controls/DataGrid/Izenda.Table';
import {
  TableDefinition,
  KEY_NEW_REPORT_DASHBOARD_NAME,
  KEY_NEW_REPORT_NAME,
  REPORT_NAME_VALIDATION_RULES,
  TEMPLATE_NAME_VALIDATION_RULES,
  DASHBOARD_NAME_VALIDATION_RULES,
  KEY_SAVE_INTO,
  KEY_CATEGORY,
  KEY_SUBCATEGORY,
  DEFAULT_REST_HEIGHT,
  DEFAULT_DATABASE_MAPPING_HEIGHT,
  DEFAULT_TABLE_HEIGHT,
  HINT_MESSAGE
} from 'logic/data/Izenda.ImportConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Select from 'share/components/controls/multiSelectBox/Izenda.Select';
import Input from 'share/components/controls/Izenda.Input';
import actions from './Izenda.ImportListActions';
import connect from 'share/enhancers/Izenda.Connect';
import UiState from 'logic/Izenda.UiState';
import { isNil, sortBy, get, find, forEach } from 'lodash/fp';
import { propTypes as MobxPropTypes } from 'mobx-react';

const InputValidator = Validator()(Input);
const SelectValidator = Validator()(Select);
const TABLE_DEF = TableDefinition.ImportSourceType;

@connect(actions)
export default class ImportList extends Component {
  static propTypes = {
    height: PropTypes.number,
    dataSource: MobxPropTypes.arrayOrObservableArray,
    saveInto: MobxPropTypes.arrayOrObservableArray,
    loadCategories: PropTypes.func,
    loadSubCategories: PropTypes.func,
    store: MobxPropTypes.observableObject
  };

  constructor(props: any) {
    super(props);
    this.rowKey = this.props.isDashboardTab ? 'dashboard' : 'report';
  }

  renderFileName = ({ name }): any => {
    return (
      <span className="truncate overflow-text" title={name}>
        {name}
      </span>
    );
  };

  renderType = (data: any) => {
    const type = this.props.getType(data);
    return (
      <span className="truncate overflow-text" title={type}>
        {type}
      </span>
    );
  };

  renderNewReportName = (data: any) => {
    const { isDashboardTab } = this.props;
    const key = isDashboardTab ? KEY_NEW_REPORT_DASHBOARD_NAME : KEY_NEW_REPORT_NAME;
    const validationRules = data.isReport
      ? data.content.type === 0 ? REPORT_NAME_VALIDATION_RULES : TEMPLATE_NAME_VALIDATION_RULES
      : DASHBOARD_NAME_VALIDATION_RULES;

    return (
      <InputValidator
        className="form-control validator"
        value={data.content.name}
        onChange={this.props.updateName.bind(this.props, data)}
        validations={validationRules}
        field={key}
        group={`${this.rowKey}.${data.content.index}`}
      />
    );
  };

  renderSaveInto = (data: any) => {
    const { saveInto } = this.props;

    return (
      <SelectValidator
        simpleValue
        labelKey="name"
        valueKey="id"
        options={saveInto}
        value={data.saveIntoId}
        onChange={this.props.updateSaveIntoId.bind(this.props, data)}
        tooltipPosition="bottom"
        field={KEY_SAVE_INTO}
        group={`${this.rowKey}.${data.content.index}`}
      />
    );
  };

  renderCategory = (data: any) => {
    const { store } = this.props;
    const categories = sortBy(cat => cat.name.toLowerCase(), data.categories);
    const importSourceCat = get('model.importSourceCat',store);

    return (
      <SelectValidator
        simpleValue
        disabled = {importSourceCat}
        options={categories}
        labelKey="name"
        valueKey="id"
        value={data.content.categoryId}
        noResultsText={HINT_MESSAGE.CATEGORY}
        onChange={this.props.updateCategory.bind(this.props, data)}
        onAddNew={store.addNewCategory.bind(store, data)}
        scalable={false}
        field={KEY_CATEGORY}
        group={`${this.rowKey}.${data.content.index}`}
      />
    );
  };

  renderSubCategory = (data: any) => {
    const { store } = this.props;
    const subCategories = sortBy(subCat => subCat.name.toLowerCase(), data.subCategories);
    const importSourceCat = get('model.importSourceCat',store);

    return (
      <SelectValidator
        simpleValue
        options={subCategories}
        labelKey="name"
        valueKey="id"
        value={data.content.subCategoryId}
        noResultsText={HINT_MESSAGE.SUB_CATEGORY}
        onChange={this.props.updateSubCategory.bind(this.props, data)}
        onAddNew={store.addNewSubCategory.bind(store, data)}
        scalable={false}
        disabled={isNil(data.content.categoryId) || importSourceCat}
        field={KEY_SUBCATEGORY}
        group={`${this.rowKey}.${data.content.index}`}
      />
    );
  };

  renderGear = (data: any) => {
    return (
      <div className="u_flexHorizontal u_flexCenterAlignItems">
        <a onClick={this.props.openDetailFilePopup.bind(this.props, data)}>
          <span className="icon-info-circled" />
        </a>
        {(!this.props.isDashboardTab || !data.isReport) && (
          <span className="icon-cancel" onClick={this.props.removeFile.bind(this.props, data)} />
        )}
      </div>
    );
  };

  render() {
    const { height, dataSource, isDashboardTab } = this.props;

    const newHeight =
      height - (DEFAULT_REST_HEIGHT + DEFAULT_DATABASE_MAPPING_HEIGHT) > DEFAULT_TABLE_HEIGHT
        ? height - (DEFAULT_REST_HEIGHT + DEFAULT_DATABASE_MAPPING_HEIGHT)
        : DEFAULT_TABLE_HEIGHT;

    return (
      <Table minHeight={newHeight} maxHeight={newHeight} dataSource={dataSource} sortInfo staticRender>
        <Column {...TABLE_DEF['fileName']} cell={this.renderFileName} />
        <Column {...TABLE_DEF['type']} cell={this.renderType} />
        <Column
          {...TABLE_DEF[isDashboardTab ? 'newReportOrDashboardName' : 'newReportName']}
          cell={this.renderNewReportName}
        />
        {isNil(UiState.tenantId) && <Column {...TABLE_DEF['saveInto']} cell={this.renderSaveInto} />}
        <Column {...TABLE_DEF['category']} cell={this.renderCategory} />
        <Column {...TABLE_DEF['subCategory']} cell={this.renderSubCategory} />
        <Column {...TABLE_DEF['gear']} cell={this.renderGear} />
      </Table>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/DataSetup/Import/Izenda.ImportList.js